import { useMeContext } from '@shared/contexts/hooks/useMeContext';
import { createContext, useContext, useMemo, useState } from 'react';
import { FeedValue, useFeed } from '../features/Feed/hooks/useFeed';
import { BuildJobHandler } from '../features/Feed/hooks/useSuggestedActions';
import { FeedFilterOption, FeedFilterParams } from '../features/Feed/types';

type FeedContextType = {
  feed: FeedValue;
  hasUnconfirmedQuote: boolean;
  hasUnconfirmedOrder: boolean;
  filter: FeedFilterParams;
};

export const FeedContext = createContext<FeedContextType>({
  feed: {
    items: [],
    suggestedActions: [],
    refresh: () => Promise.resolve(),
    executeAction: () => Promise.resolve(),
    getJobSuggestedActions: () => [],
  },
  filter: {
    selectedOption: FeedFilterOption.ACTIVITY,
    setSelectedOption: () => {},
    filterOptions: [FeedFilterOption.ACTIVITY],
  },
  hasUnconfirmedQuote: false,
  hasUnconfirmedOrder: false,
});

export const FeedContextProvider = ({
  handleSendToVooma,
  disableTransactions,
  children,
  hasUnconfirmedQuote,
  hasUnconfirmedOrder,
}: {
  children: JSX.Element;
  disableTransactions: boolean;
  handleSendToVooma: BuildJobHandler;
  hasUnconfirmedQuote: boolean;
  hasUnconfirmedOrder: boolean;
}) => {
  const [selectedOption, setSelectedOption] = useState<FeedFilterOption>(
    FeedFilterOption.ACTIVITY
  );
  const { hasOrders, hasQuotes } = useMeContext();

  const feed = useFeed({
    handleSendToVooma,
    disableTransactions,
  });

  const options = useMemo(() => {
    const filterOptions = [FeedFilterOption.ACTIVITY];

    if (hasQuotes) {
      filterOptions.push(FeedFilterOption.QUOTES);
    }
    if (hasOrders) {
      filterOptions.push(FeedFilterOption.ORDERS);
    }
    return filterOptions;
  }, [hasOrders, hasQuotes]);

  return (
    <FeedContext.Provider
      value={{
        feed,
        hasUnconfirmedQuote,
        hasUnconfirmedOrder,
        filter: {
          selectedOption,
          setSelectedOption,
          filterOptions: options,
        },
      }}
    >
      {children}
    </FeedContext.Provider>
  );
};

export function useFeedContext() {
  const context = useContext(FeedContext);
  if (!context) {
    throw new Error(
      'useFeedContext must be used within a FeedContext.Provider'
    );
  }
  return context;
}
